import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CheckCircle2, Loader2, Users, XCircle } from "lucide-react";

type Collaborator = {
  id: string;
  status: "pending" | "active" | "expired" | "revoked";
  collaborator_email: string | null;
  invited_by: string | null;
  created_at: string;
  approved_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
};

type ResolvedStatus = Exclude<Collaborator["status"], "pending">;

const STATUS_STYLES: Record<ResolvedStatus, string> = {
  active: "bg-green-100 text-green-800",
  expired: "bg-muted text-muted-foreground",
  revoked: "bg-red-100 text-red-800",
};

const STATUS_LABELS: Record<ResolvedStatus, string> = {
  active: "Access active",
  expired: "Request expired",
  revoked: "Access revoked",
};

const fmt = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

// Owner-equivalent list of Dibs collaborators on the active business, with
// approval controls for pending rows and revocation for active access.
const CollaboratorAccessCard = () => {
  const [rows, setRows] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await (supabase as any)
      .from("support_collaborators")
      .select(
        "id, status, collaborator_email, invited_by, created_at, approved_at, expires_at, revoked_at"
      )
      .order("created_at", { ascending: false });
    if (!error && data) setRows(data as Collaborator[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = useCallback(
    async (grantId: string) => {
      setProcessingId(grantId);
      try {
        const { data, error } = await supabase.functions.invoke(
          "collaborator-revoke",
          { body: { grantId } }
        );
        if (error || (data && data.success === false)) {
          const msg =
            (data && data.error) || "Could not revoke access. Please try again.";
          toast({ title: "Revoke failed", description: msg, variant: "destructive" });
          return;
        }
        toast({ title: "Access revoked" });
        await load();
      } finally {
        setProcessingId(null);
      }
    },
    [load]
  );

  const decidePending = useCallback(
    async (grantId: string, decision: "approve" | "reject") => {
      setProcessingId(grantId);
      try {
        const { data, error } = await supabase.functions.invoke(
          "collaborator-respond",
          { body: { grantId, decision: decision === "reject" ? "deny" : "approve" } }
        );
        if (error || (data && data.success === false)) {
          const msg =
            (data && data.error) ||
            `Could not ${decision} this request. Please try again.`;
          toast({
            title: decision === "approve" ? "Approval failed" : "Rejection failed",
            description: msg,
            variant: "destructive",
          });
          return;
        }
        toast({ title: decision === "approve" ? "Access approved" : "Request rejected" });
        await load();
      } catch {
        toast({
          title: decision === "approve" ? "Approval failed" : "Rejection failed",
          description: `Could not ${decision} this request. Please try again.`,
          variant: "destructive",
        });
      } finally {
        setProcessingId(null);
      }
    },
    [load]
  );

  return (
    <main className="space-y-8">
      <header className="min-w-0 space-y-1">
        <h1 className="text-xl font-heading font-semibold text-foreground">
          Support access
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Dibs support specialists you've approved to help set up your account.
          They sign in with their own Dibs account and can access your whole business,
          not just one outlet. You can revoke their access at any time.
        </p>
      </header>

      <section aria-labelledby="support-specialists-heading">
        <div className="flex items-center gap-4">
          <h2
            id="support-specialists-heading"
            className="shrink-0 font-heading text-base font-semibold text-foreground"
          >
            Support specialists
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>

        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading support access…
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-4 flex min-h-36 flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 px-6 py-8 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Users className="h-5 w-5" />
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground">No support access</p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              When you approve a request from the Dibs team, the specialist will appear here.
            </p>
          </div>
        ) : (
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((c) => {
              const live = c.status === "pending" || c.status === "active";
              const initial = (c.collaborator_email?.trim().charAt(0) || "D").toUpperCase();

              return (
                <li
                  key={c.id}
                  className="flex min-w-0 flex-col rounded-lg border bg-card p-4 shadow-none"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground">
                      {initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {c.collaborator_email || "Dibs support specialist"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Requested {fmt(c.created_at)}
                        {c.status === "active" && c.approved_at && (
                          <> · Approved {fmt(c.approved_at)}</>
                        )}
                        {c.status === "revoked" && c.revoked_at && (
                          <> · Revoked {fmt(c.revoked_at)}</>
                        )}
                      </p>
                      {c.status !== "pending" && (
                        <span
                          className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[c.status]}`}
                        >
                          {STATUS_LABELS[c.status]}
                        </span>
                      )}
                    </div>
                  </div>

                  {c.status === "pending" && (
                    <div className="mt-4 grid w-full grid-cols-2 gap-2.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-full border-red-200 text-xs text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                        onClick={() => decidePending(c.id, "reject")}
                        disabled={processingId === c.id}
                      >
                        <XCircle className="mr-1 h-3.5 w-3.5" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 w-full bg-emerald-600 text-xs font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
                        onClick={() => decidePending(c.id, "approve")}
                        disabled={processingId === c.id}
                      >
                        {processingId === c.id ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                        )}
                        Approve
                      </Button>
                    </div>
                  )}

                  {c.status !== "pending" && live ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-auto self-start"
                      onClick={() => revoke(c.id)}
                      disabled={processingId === c.id}
                    >
                      {processingId === c.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Revoking…
                        </>
                      ) : (
                        "Revoke access"
                      )}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
};

export default CollaboratorAccessCard;
